import {
  signInWithEmailAndPassword,
  signOut,
  signInWithCustomToken,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';

// ─── Errores tipados ────────────────────────────────────────

export class AuthError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

// Roles con acceso al PORTAL MÉDICO. El proyecto Firebase se comparte con la
// app móvil de pacientes (mismo pool de usuarios); sin este gate un paciente
// podría autenticarse en el portal. (SEC-10)
const PORTAL_ROLES = ['medico', 'admin', 'supervisor', 'notificador', 'visor'];

function tienePortalRol(role: unknown): boolean {
  return typeof role === 'string' && PORTAL_ROLES.includes(role);
}

// ─── Login email/password ───────────────────────────────────

export async function loginEmailPassword(
  email: string,
  password: string,
): Promise<User> {
  const result = await signInWithEmailAndPassword(auth, email, password);
  // Forzar refresh para garantizar claims actualizados.
  // Sin el `true`, Firebase puede devolver un token cacheado del session anterior
  // que aún tenga claims antiguos (p.ej. salaIds desactualizados tras una
  // reasignación de salas).
  await result.user.getIdToken(true);
  const tokenResult = await result.user.getIdTokenResult();

  if (tokenResult.claims.activo === false) {
    await signOut(auth);
    throw new AuthError(
      'CUENTA_DESACTIVADA',
      'Su cuenta ha sido desactivada. Contacte al administrador.',
    );
  }

  // Gate de rol de portal: sólo los roles de PORTAL_ROLES entran. (SEC-10)
  if (!tienePortalRol(tokenResult.claims.role)) {
    await signOut(auth);
    throw new AuthError(
      'SIN_ACCESO_PORTAL',
      'Esta cuenta no tiene acceso al portal médico.',
    );
  }

  if (!result.user.emailVerified) {
    await signOut(auth);
    throw new AuthError(
      'EMAIL_NO_VERIFICADO',
      'Debe verificar su email antes de iniciar sesion. Revise su bandeja de entrada.',
    );
  }

  return result.user;
}

// ─── Registro ───────────────────────────────────────────────

export async function registrarMedico(
  email: string,
  password: string,
  confirmarPassword: string,
): Promise<{ message: string; requiresEmailVerification: boolean }> {
  const fn = httpsCallable(functions, 'portal_registroEmailPassword');
  const result = await fn({ email, password, confirmarPassword });
  return result.data as { message: string; requiresEmailVerification: boolean };
}

// ─── Magic Link ─────────────────────────────────────────────

export async function solicitarMagicLink(email: string): Promise<string> {
  const fn = httpsCallable(functions, 'portal_enviarMagicLink');
  const result = await fn({ email });
  return (result.data as { message: string }).message;
}

/**
 * Verifica el PIN enviado por correo. NO abre sesión.
 *
 * El PIN es solo el primer factor: quien controle el buzón lo tiene. La función
 * devuelve un desafío que hay que canjear en `dosFactoresService` con el código
 * de la aplicación de autenticación; ahí es donde nace la sesión.
 *
 * El gate de rol y el de cuenta desactivada se aplican ahora en el servidor,
 * antes de emitir el desafío (ver `functions/src/auth/segundoFactor.ts`).
 */
export async function verificarMagicPin(
  email: string,
  pin: string,
): Promise<{
  desafioId?: string;
  requiereCodigo?: boolean;
  requiereEnrolamiento?: boolean;
  entroDirecto?: boolean;
}> {
  const fn = httpsCallable(functions, 'portal_verificarMagicPin');
  const result = await fn({ email, pin });
  const d = result.data as {
    desafioId?: string;
    requiereCodigo?: boolean;
    requiereEnrolamiento?: boolean;
    customToken?: string;
  };
  // Persona exenta: el servidor ya entregó la sesión, no hay desafío que canjear.
  if (d.customToken) {
    const cred = await signInWithCustomToken(auth, d.customToken);
    await cred.user.getIdToken(true);
    return { entroDirecto: true };
  }
  return d;
}

export async function completarLoginMagicLink(): Promise<User | null> {
  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return null;
  }

  let email = localStorage.getItem('minsalud_magic_link_email');
  if (!email) {
    email = window.prompt('Ingrese su correo electronico para confirmar:');
  }
  if (!email) return null;

  const result = await signInWithEmailLink(auth, email, window.location.href);
  localStorage.removeItem('minsalud_magic_link_email');
  return result.user;
}

// ─── Reset password ─────────────────────────────────────────

export async function solicitarResetPassword(email: string): Promise<void> {
  const fn = httpsCallable(functions, 'portal_solicitarResetPassword');
  await fn({ email });
}

export async function verificarResetPin(email: string, pin: string): Promise<void> {
  const fn = httpsCallable(functions, 'portal_verificarResetPin');
  await fn({ email, pin });
}

export async function confirmarResetPassword(
  email: string,
  pin: string,
  newPassword: string,
): Promise<void> {
  const fn = httpsCallable(functions, 'portal_confirmarResetPassword');
  await fn({ email, pin, newPassword });
}

// ─── Logout ─────────────────────────────────────────────────

export async function logout(): Promise<void> {
  await signOut(auth);
}
